import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, ShieldCheck, FileSignature, UserCheck, LogOut, CheckCircle2, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useDashboard } from '@/context/DashboardContext';
import { dashboardApi } from '@/services/dashboardApi';
import { LEGAL_DOC_CLASSES } from '@/components/dashboard/legalDocStyles';
import { useSiteLang } from '@/components/site/SiteLang';

/*
  Datos del socio tras la compra (Samuel, 06/10/2026):
    1. Mini KYC: nombre completo, documento, dirección fiscal, provincia y país.
    2. Aceptar los Términos y Condiciones vigentes.
  Decisión 14/10/2026: el KYC y los T&C NO bloquean el panel; se piden con un aviso y el socio los
  completa cuando quiera. Lo único que bloquea es verificar el email (cuentas nuevas).
  El equipo (admin) está exento. La aceptación queda en "Mis documentos" con copia del texto, huella, fecha e IP.
*/
const DISMISS_KEY = 'fhc_legal_reminder_dismissed';

const T = {
  es: {
    docTypes: [
      { value: 'dni', label: 'DNI (España)' },
      { value: 'nie', label: 'NIE (España)' },
      { value: 'pasaporte', label: 'Pasaporte' },
      { value: 'otro', label: 'Documento oficial de mi país' },
    ],
    stepData: 'Tus datos',
    stepTerms: 'Términos y Condiciones',
    kycSaveError: 'No hemos podido guardar tus datos',
    kycIntro: 'Necesitamos estos datos para emitir tus facturas y para que la aceptación de los Términos quede a tu nombre.',
    fullName: 'Nombre y apellidos',
    document: 'Documento',
    docNumber: 'Número de documento',
    address: 'Dirección fiscal',
    addressPh: 'Calle, número, código postal y ciudad',
    province: 'Provincia / Estado',
    country: 'País',
    saveContinue: 'Guardar y continuar',
    docLoadError: 'No hemos podido cargar el documento',
    accepted: (title) => `${title} aceptados. Los tienes en "Mis documentos".`,
    acceptError: 'No se pudo registrar la aceptación',
    docFallback: 'Documento',
    version: 'Versión',
    spanishNotice: null,
    scrollHint: 'Desplázate hasta el final del documento para poder aceptarlo.',
    acceptLabel: (title, v) => `He leído y acepto los ${title} de Freedom Homes Club — versión ${v}.`,
    signAs: 'Firmarás como',
    editData: 'Editar mis datos',
    acceptSign: 'Aceptar y firmar',
    emailVerified: 'Email verificado',
    verifyError: 'No hemos podido verificar el código',
    codeResent: 'Te hemos enviado un código nuevo.',
    sendError: 'No hemos podido enviar el código',
    codeSent: (email) => <>Te hemos enviado un código de 6 cifras a <strong className="text-white">{email}</strong>. Escríbelo aquí para confirmar que el email es tuyo. Si no te llega en unos minutos, pulsa Reenviar código.</>,
    devCode: 'Entorno de pruebas (sin servidor de correo): tu código es',
    verifyEmail: 'Verificar email',
    resend: '¿No te ha llegado? Reenviar código (revisa también la carpeta de spam)',
    verifyTitle: 'Verifica tu email',
    verifySub: 'Un paso rápido para proteger tu cuenta.',
    signOut: 'Salir',
    reminderTerms: 'Te falta aceptar los Términos y Condiciones de tu membresía.',
    reminderAll: 'Completa tus datos de socio (documento de identidad y dirección fiscal) y acepta los Términos y Condiciones.',
    completeNow: 'Completar ahora',
    later: 'Más tarde',
    gateTitle: 'Tus datos de socio',
    gateSub: 'Completa tus datos y acepta los Términos. Puedes hacerlo ahora o más tarde.',
    notNow: 'Ahora no',
  },
  en: {
    docTypes: [
      { value: 'dni', label: 'DNI (Spain)' },
      { value: 'nie', label: 'NIE (Spain)' },
      { value: 'pasaporte', label: 'Passport' },
      { value: 'otro', label: 'Official ID from my country' },
    ],
    stepData: 'Your details',
    stepTerms: 'Terms and Conditions',
    kycSaveError: 'We couldn’t save your details',
    kycIntro: 'We need these details to issue your invoices and to record your acceptance of the Terms in your name.',
    fullName: 'Full name',
    document: 'ID document',
    docNumber: 'Document number',
    address: 'Tax address',
    addressPh: 'Street, number, postcode and town',
    province: 'Province / State',
    country: 'Country',
    saveContinue: 'Save and continue',
    docLoadError: 'We couldn’t load the document',
    accepted: (title) => `${/t[ée]rminos y condiciones/i.test(title || '') ? 'Terms and Conditions' : title} accepted. You’ll find a copy in ‘My documents’.`,
    acceptError: 'We couldn’t record your acceptance',
    docFallback: 'Document',
    version: 'Version',
    spanishNotice: 'Our Terms and Conditions are published in Spanish. The Spanish text is the legally binding version.',
    scrollHint: 'Scroll to the end of the document to accept it.',
    acceptLabel: (title, v) => `I have read and accept the Freedom Homes Club ${/t[ée]rminos y condiciones/i.test(title || '') ? 'Terms and Conditions' : title}, version ${v}.`,
    signAs: 'You’ll sign as',
    editData: 'Edit my details',
    acceptSign: 'Accept and sign',
    emailVerified: 'Email verified',
    verifyError: 'We couldn’t verify the code',
    codeResent: 'We’ve sent you a new code.',
    sendError: 'We couldn’t send the code',
    codeSent: (email) => <>We’ve sent a 6-digit code to <strong className="text-white">{email}</strong>. Enter it here to confirm this email address is yours. If it hasn’t arrived within a few minutes, tap Resend code.</>,
    devCode: 'Test environment (no mail server): your code is',
    verifyEmail: 'Verify email',
    resend: 'Not received it? Resend code (check your spam folder too)',
    verifyTitle: 'Verify your email',
    verifySub: 'A quick step to keep your account secure.',
    signOut: 'Sign out',
    reminderTerms: 'You still need to accept the Terms and Conditions of your membership.',
    reminderAll: 'Complete your member details (ID document and tax address) and accept the Terms and Conditions.',
    completeNow: 'Complete now',
    later: 'Later',
    gateTitle: 'Your member details',
    gateSub: 'Complete your details and accept the Terms. You can do it now or later.',
    notNow: 'Not now',
  },
};
const useT = () => { const { lang } = useSiteLang(); return T[lang] || T.es; };
const input = 'w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#c9a84c]/50';
const label = 'block text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2';

function Steps({ step }) {
  const tr = useT();
  const items = [{ n: 1, t: tr.stepData, icon: UserCheck }, { n: 2, t: tr.stepTerms, icon: FileSignature }];
  return (
    <div className="flex items-center gap-3 mb-8">
      {items.map((it, i) => (
        <div key={it.n} className="flex items-center gap-3">
          <div className={`flex items-center gap-2 text-xs font-medium ${step >= it.n ? 'text-[#c9a84c]' : 'text-gray-400'}`}>
            <span className={`w-7 h-7 rounded-full flex items-center justify-center border ${step > it.n ? 'bg-[#c9a84c] border-[#c9a84c] text-[#0a1225]' : step === it.n ? 'border-[#c9a84c]' : 'border-white/10'}`}>
              {step > it.n ? <CheckCircle2 size={14} /> : it.n}
            </span>
            {it.t}
          </div>
          {i === 0 && <span className="w-8 h-px bg-white/10" />}
        </div>
      ))}
    </div>
  );
}

function KycForm({ initial, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const t = useT();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const { kyc } = await dashboardApi.saveKyc(form);
      onSaved(kyc);
    } catch (err) {
      setError(err.message || t.kycSaveError);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="kyc-form">
      <p className="text-sm text-gray-400 leading-relaxed">
        {t.kycIntro}
      </p>
      <div>
        <label className={label}>{t.fullName}</label>
        <input className={input} value={form.full_name} onChange={set('full_name')} required autoComplete="name" data-testid="kyc-name" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4">
        <div>
          <label className={label}>{t.document}</label>
          <select className={`${input} appearance-none`} value={form.document_type} onChange={set('document_type')} data-testid="kyc-doctype">
            {t.docTypes.map((d) => <option key={d.value} value={d.value} className="bg-[#0a1225]">{d.label}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>{t.docNumber}</label>
          <input className={input} value={form.document_number} onChange={set('document_number')} required data-testid="kyc-docnumber" />
        </div>
      </div>
      <div>
        <label className={label}>{t.address}</label>
        <input className={input} value={form.fiscal_address} onChange={set('fiscal_address')} required autoComplete="street-address" placeholder={t.addressPh} data-testid="kyc-address" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={label}>{t.province}</label>
          <input className={input} value={form.province} onChange={set('province')} required autoComplete="address-level1" data-testid="kyc-province" />
        </div>
        <div>
          <label className={label}>{t.country}</label>
          <input className={input} value={form.country} onChange={set('country')} required autoComplete="country-name" data-testid="kyc-country" />
        </div>
      </div>
      {error && <p className="text-sm text-red-400" role="alert">{error}</p>}
      <button type="submit" disabled={saving}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#c9a84c] text-[#0a1225] font-semibold text-sm py-3.5 hover:bg-[#d8b85c] disabled:opacity-60" data-testid="kyc-submit">
        {saving && <Loader2 size={16} className="animate-spin" />} {t.saveContinue}
      </button>
    </form>
  );
}

function TermsStep({ kyc, pendingDoc, onEditData, onSigned }) {
  const [doc, setDoc] = useState(null);
  const [atEnd, setAtEnd] = useState(false);
  const [checked, setChecked] = useState(false);
  const [signing, setSigning] = useState(false);
  const boxRef = useRef(null);
  const t = useT();

  useEffect(() => {
    dashboardApi.legalCurrentDocument(pendingDoc.type).then(setDoc).catch(() => toast.error(t.docLoadError));
  }, [pendingDoc.type]);

  const onScroll = () => {
    const el = boxRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setAtEnd(true);
  };
  useEffect(() => { const el = boxRef.current; if (doc && el && el.scrollHeight <= el.clientHeight + 24) setAtEnd(true); }, [doc]);

  const sign = async () => {
    setSigning(true);
    try {
      await dashboardApi.acceptLegal(doc.type, doc.version);
      toast.success(t.accepted(doc.title));
      onSigned();
    } catch (err) {
      toast.error(err.message || t.acceptError);
      setSigning(false);
    }
  };

  const docLabel = (t.docTypes.find((d) => d.value === kyc.document_type) || {}).label?.replace(/ \(.*\)$/, '') || t.docFallback;

  return (
    <div data-testid="terms-step">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-white font-semibold">{pendingDoc.title}</h3>
        <span className="text-[11px] text-gray-400">{t.version} {pendingDoc.version_label}</span>
      </div>
      {t.spanishNotice && <p className="text-[11px] text-gray-500 mb-2" data-testid="terms-spanish-notice">{t.spanishNotice}</p>}
      <div ref={boxRef} onScroll={onScroll} tabIndex={0}
        className="h-[46vh] overflow-y-auto rounded-xl border border-white/10 bg-black/20 px-5 py-4" data-testid="terms-box">
        {doc ? <div className={LEGAL_DOC_CLASSES} dangerouslySetInnerHTML={{ __html: doc.html }} />
             : <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#c9a84c]" /></div>}
      </div>
      {!atEnd && doc && <p className="text-[11px] text-gray-400 mt-2">{t.scrollHint}</p>}

      <label className={`flex items-start gap-3 mt-5 text-sm ${atEnd ? 'text-gray-300 cursor-pointer' : 'text-gray-400 cursor-not-allowed'}`}>
        <input type="checkbox" className="mt-1 accent-[#c9a84c]" disabled={!atEnd} checked={checked} onChange={(e) => setChecked(e.target.checked)} data-testid="terms-check" />
        <span>{t.acceptLabel(pendingDoc.title, pendingDoc.version_label)}</span>
      </label>

      <div className="mt-4 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-xs text-gray-400 flex flex-wrap items-center justify-between gap-2">
        <span>{t.signAs} <strong className="text-white">{kyc.full_name}</strong> · {docLabel} {kyc.document_number}</span>
        <button type="button" onClick={onEditData} className="text-[#c9a84c] hover:underline">{t.editData}</button>
      </div>

      <button type="button" onClick={sign} disabled={!checked || signing || !doc}
        className="w-full mt-5 inline-flex items-center justify-center gap-2 rounded-xl bg-[#c9a84c] text-[#0a1225] font-semibold text-sm py-3.5 hover:bg-[#d8b85c] disabled:opacity-40" data-testid="terms-sign">
        {signing && <Loader2 size={16} className="animate-spin" />} {t.acceptSign}
      </button>
    </div>
  );
}

function EmailStep({ email, onVerified }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const t = useT();
  const [devCode, setDevCode] = useState(() => { try { return sessionStorage.getItem('fhc_dev_code') || ''; } catch { return ''; } });

  const verify = async (e) => {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      await dashboardApi.verifyEmailCode(code);
      try { sessionStorage.removeItem('fhc_dev_code'); } catch { /* sin almacenamiento */ }
      toast.success(t.emailVerified);
      onVerified();
    } catch (err) { setError(err.message || t.verifyError); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    setError(''); setInfo('');
    try {
      const r = await dashboardApi.sendEmailCode();
      if (r.dev_code) setDevCode(r.dev_code);
      setInfo(t.codeResent);
    } catch (err) { setError(err.message || t.sendError); }
  };

  return (
    <form onSubmit={verify} className="space-y-4" data-testid="email-step">
      <p className="text-sm text-gray-400 leading-relaxed">
        {t.codeSent(email)}
      </p>
      <input className={`${input} text-center text-2xl tracking-[0.5em] font-semibold`} value={code} inputMode="numeric" autoComplete="one-time-code"
        maxLength={6} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="••••••" data-testid="email-code" />
      {devCode && <p className="text-[11px] text-amber-300/80">{t.devCode} {devCode}</p>}
      {error && <p className="text-sm text-red-400" role="alert">{error}</p>}
      {info && <p className="text-sm text-emerald-400">{info}</p>}
      <button type="submit" disabled={busy || code.length !== 6}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#c9a84c] text-[#0a1225] font-semibold text-sm py-3.5 hover:bg-[#d8b85c] disabled:opacity-40" data-testid="email-verify">
        {busy && <Loader2 size={16} className="animate-spin" />} {t.verifyEmail}
      </button>
      <button type="button" onClick={resend} className="w-full text-xs text-gray-400 hover:text-gray-300 py-1" data-testid="email-resend">
        {t.resend}
      </button>
    </form>
  );
}

export default function LegalGate({ children }) {
  const { user, membership, isAdmin, logout } = useDashboard();
  const t = useT();
  const active = membership?.active_membership;
  const [status, setStatus] = useState(null);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const later = () => {
    setOpen(false);
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* sin almacenamiento: solo se oculta ahora */ }
  };

  const load = useCallback(async () => {
    try { setStatus(await dashboardApi.legalStatus()); }
    catch { setStatus({ required: false }); }   // si el servicio falla no bloqueamos el panel
  }, []);

  useEffect(() => { if (user && !isAdmin) load(); else setStatus(null); }, [user, active, isAdmin, load]);

  if (!status || !status.required) return children;

  if (status.email_verified === false) {
    return (
      <div className="max-w-md mx-auto py-10" data-testid="legal-gate">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#c9a84c]/10 border border-[#c9a84c]/30 flex items-center justify-center flex-shrink-0">
              <MailCheck size={20} className="text-[#c9a84c]" />
            </div>
            <div>
              <h2 className="text-white text-xl font-semibold">{t.verifyTitle}</h2>
              <p className="text-xs text-gray-400 mt-0.5">{t.verifySub}</p>
            </div>
          </div>
          <button type="button" onClick={logout} className="text-xs text-gray-400 hover:text-gray-300 inline-flex items-center gap-1.5"><LogOut size={13} /> {t.signOut}</button>
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
          <EmailStep email={user?.email} onVerified={load} />
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <>
        {!dismissed && (
          <div className="mb-5 rounded-2xl border border-[#c9a84c]/25 bg-[#c9a84c]/[0.06] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="legal-reminder">
            <ShieldCheck size={20} className="text-[#c9a84c] flex-shrink-0" />
            <p className="text-sm text-gray-300 flex-1">
              {status.kyc_complete
                ? t.reminderTerms
                : t.reminderAll}
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setOpen(true)} data-testid="legal-open"
                className="rounded-xl bg-[#c9a84c] text-[#0a1225] font-semibold text-sm px-4 py-2 hover:bg-[#d8b85c]">{t.completeNow}</button>
              <button type="button" onClick={later} data-testid="legal-later"
                className="rounded-xl border border-white/10 text-gray-300 text-sm px-4 py-2 hover:bg-white/[0.04]">{t.later}</button>
            </div>
          </div>
        )}
        {children}
      </>
    );
  }

  const kyc = status.kyc || {};
  const step = !status.kyc_complete || editing ? 1 : 2;
  const initial = {
    full_name: kyc.full_name || status.defaults?.full_name || '',
    document_type: kyc.document_type || 'dni',
    document_number: kyc.document_number || '',
    fiscal_address: kyc.fiscal_address || '',
    province: kyc.province || '',
    country: kyc.country || 'España',
  };

  return (
    <div className="max-w-2xl mx-auto py-6" data-testid="legal-gate">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#c9a84c]/10 border border-[#c9a84c]/30 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={20} className="text-[#c9a84c]" />
          </div>
          <div>
            <h2 className="text-white text-xl font-semibold">{t.gateTitle}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{t.gateSub}</p>
          </div>
        </div>
        <button type="button" onClick={later} className="text-xs text-gray-400 hover:text-gray-300 flex-shrink-0" data-testid="legal-close">
          {t.notNow}
        </button>
      </div>
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
        <Steps step={step} />
        {step === 1
          ? <KycForm initial={initial} onSaved={(saved) => { setStatus((s) => ({ ...s, kyc: saved, kyc_complete: true })); setEditing(false); if (!status.pending?.length) load(); }} />
          : status.pending?.length
            ? <TermsStep kyc={kyc} pendingDoc={status.pending[0]} onEditData={() => setEditing(true)} onSigned={load} />
            : <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#c9a84c]" size={22} /></div>}
      </div>
    </div>
  );
}
